import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { parseContract } from '@/lib/api/parse';
import { requestProductPresign, type PresignResult } from '@/lib/upload';

import { toListQuery } from './filters';
import {
  orderAssigneeSchema,
  orderCustomerPageSchema,
  orderDetailSchema,
  orderListResponseSchema,
  orderReviewSchema,
  toTicketBody,
} from './schemas';
import type {
  OrderAssignee,
  OrderCustomerPage,
  OrderCustomerParams,
  OrderDetail,
  OrderListParams,
  OrderListResponse,
  OrderReview,
  OrderStatus,
  RaiseTicketValues,
} from './types';

export { toListQuery };

/** Sort + filters → the list's wire params, defaults included. The export sends the same, so the
 *  file comes out with the rows and order on screen, sorted or not (ADO 120974). */
export function ordersRequestQuery(params: Omit<OrderListParams, 'page' | 'limit'>) {
  const { sortBy = 'orderedAt', sortOrder = 'desc', ...filters } = params;
  return { sortBy, sortOrder, ...toListQuery(filters) };
}

/** Paginated/sorted/filtered/searchable order list, pre-placement orders included (feature 122355). */
export async function listOrders(params: OrderListParams): Promise<OrderListResponse> {
  const { page, limit, ...rest } = params;
  const { data, meta } = await apiClient.getList(endpoints.orders.list, {
    params: { page, limit, ...ordersRequestQuery(rest) },
  });
  return parseContract(orderListResponseSchema, { data, meta }, endpoints.orders.list);
}

/** Full order detail. 404 rejects as `ApiError` (status 404) — the page renders not-found. */
export async function getOrder(id: string): Promise<OrderDetail> {
  return parseContract(
    orderDetailSchema,
    await apiClient.get(endpoints.orders.detail(id)),
    endpoints.orders.detail(id),
  );
}

/** Distinct cities present on orders (City filter options). Loaded once. */
export async function listOrderCities(): Promise<string[]> {
  const data = await apiClient.get<string[]>(endpoints.orders.metaCities);
  return data.map(String);
}

/** One page of the server-searched customer typeahead. Cursor-paginated (sort `name` asc,
 *  opaque `base64(name + customerId)` cursor); the cursor is only valid for its `search` term.
 *  `hasOrder=true` scopes the shared products-admin meta endpoint to customers who have at least
 *  one visible order — the set the orders listing filter is meant to offer (ADO 112770). */
export async function listOrderCustomers({
  search,
  cursor,
  limit,
}: OrderCustomerParams): Promise<OrderCustomerPage> {
  const page = await apiClient.getList(endpoints.orders.metaCustomers, {
    params: { search, limit, hasOrder: true, ...(cursor ? { cursor } : {}) },
  });
  return orderCustomerPageSchema.parse(page);
}

/** The stored invoice PDF and the name the server gave it (ADO 117767). Keyed on `invoiceId` from
 *  the order detail payload — there is no order-scoped invoice route. The bytes come back directly;
 *  this is not a presigned url, so ADR-0002 does not apply here. */
export async function getOrderInvoice(
  invoiceId: string,
): Promise<{ blob: Blob; contentDisposition: string | null }> {
  return apiClient.getFile(endpoints.invoices.download(invoiceId));
}

/** Applies a status transition (ADR-0015). Server enforces `STATUS_TRANSITIONS` and the
 *  delivered/pending-installation gate; both reject 409. */
export async function updateOrderStatus(id: string, status: OrderStatus): Promise<OrderDetail> {
  return orderDetailSchema.parse(await apiClient.patch(endpoints.orders.status(id), { status }));
}

/** Sets/updates the expected delivery date. Server rejects a date before the order date (409). */
export async function updateExpectedDelivery(
  id: string,
  expectedDelivery: string,
): Promise<OrderDetail> {
  return orderDetailSchema.parse(
    await apiClient.patch(endpoints.orders.expectedDelivery(id), { expectedDelivery }),
  );
}

/** Cancels an order (final state) via the status transition (ADR-0015). There is no dedicated
 *  cancel route — cancellation is `PATCH /status` → `cancelled`. Server rejects if the order is
 *  already delivered/cancelled (409). */
export async function cancelOrder(id: string): Promise<OrderDetail> {
  return updateOrderStatus(id, 'cancelled');
}

/** Assignee options for the raise-ticket form. */
export async function listOrderAssignees(): Promise<OrderAssignee[]> {
  return z.array(orderAssigneeSchema).parse(await apiClient.get(endpoints.orders.metaAssignees));
}

/** The Order's Reviews: per-device Ratings plus one order-level written review. */
export async function getOrderReviews(id: string): Promise<OrderReview> {
  return orderReviewSchema.parse(await apiClient.get(endpoints.orders.reviews(id)));
}

/** Raises a support ticket for the order (409 with `OPEN_TICKET_LIMIT_CODE` at the open-ticket cap). */
export async function createOrderTicket(id: string, values: RaiseTicketValues): Promise<void> {
  await apiClient.post(endpoints.orders.tickets(id), toTicketBody(id, values));
}

/**
 * Presigned upload url for replacement-request evidence images.
 *
 * The version story 102124 shipped sent `{ fileName, fileType, folder }` and parsed
 * `{ url, fields: { key } }`. Neither half matched the live contract, and it had no test, so it had
 * never actually run — the real endpoint answers 400 `VALIDATION_FAILED` with
 * _"property folder should not exist"_ plus `entity` and `attachmentType` required. The documented
 * body is `{ fileName, fileType, attachmentType, entity }` (Postman › Greenway › Get Pre-Signed Url),
 * which is exactly what `requestProductPresign` sends — so this now delegates rather than keeping a
 * second, drifting copy.
 *
 * `entity` decides the storage path, and BE added `replacement_request` for exactly this case
 * (story 112366, 2026-07-30) along with `customerId`/`orderId`, which are **required** for it. Before
 * that the call sent `entity: 'device'` as a placeholder and evidence landed under
 * `products/devices/images/…`. Story 112444's warranty evidence uses `warranty_claim` with the same
 * two ids.
 */
export function getOrderReplacementPresignedUrl(
  file: File,
  ids: { customerId: string; orderId: string },
): Promise<PresignResult> {
  return requestProductPresign({
    file,
    entity: 'replacement_request',
    attachmentType: 'image',
    ...ids,
  });
}

/**
 * Presigned PUT for **warranty-claim** evidence images (story 112444). Same shape as the
 * replacement wrapper above with `entity: 'warranty_claim'`, routing the upload under
 * `warranty-claims/<customerId>/<orderId>/images/`; `customerId` and `orderId` are required.
 *
 * `entity: 'warranty_claim'` is supported by the deployed backend as of 2026-08-04 (source-verified
 * against `greenway_api`'s `CatalogEntityType` enum, added 2026-07-24 — the 2026-07-31 400 this
 * comment used to record was a QA deploy lag, not an unfixed backend gap).
 */
export function getOrderWarrantyPresignedUrl(
  file: File,
  ids: { customerId: string; orderId: string },
): Promise<PresignResult> {
  return requestProductPresign({
    file,
    entity: 'warranty_claim',
    attachmentType: 'image',
    ...ids,
  });
}
